import { Badge, Box, Button, Group, Menu, Switch, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { DataTableColumn } from 'mantine-datatable';
import { useCallback, useEffect, useState } from 'react';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useCompanyDocuments } from '../../hooks/useCompanyDocuments';
import { useCreateCompanyDocument } from '../../hooks/useCreateCompanyDocument';
import { useBulkArchiveCompanyDocuments } from '../../hooks/useBulkArchiveCompanyDocuments';
import { useBulkDeleteCompanyDocuments } from '../../hooks/useBulkDeleteCompanyDocuments';
import { useOneDriveCompanyDocumentUpload } from '../../hooks/useOneDriveCompanyDocumentUpload';
import { CompanyDocument } from '../../types';
import AppendicesEmptyState from './AppendicesEmptyState';
import AppendixRowActions from './AppendixRowActions';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import ArchiveAppendixModal from './ArchiveAppendixModal';
import { useSharepoint } from 'src/data-capture/file-management/drop-zone/useSharepoint';
import { useSharePointPicker } from 'src/data-capture/file-management/drop-zone/SharePointProvider';
import { PickedItem } from 'src/data-capture/file-management/drop-zone/SharePointButton';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AppendicesListPage() {
  const { documents, isLoading: isLoadingDocuments } = useCompanyDocuments();
  const { createCompanyDocument, isLoading: isUploading } = useCreateCompanyDocument();
  const { bulkArchiveCompanyDocuments, isLoading: isArchiving } = useBulkArchiveCompanyDocuments();
  const { bulkDeleteCompanyDocuments, isLoading: isDeleting } = useBulkDeleteCompanyDocuments();
  const { createLink } = useFileTemporaryLinkMutation();
  const { uploadOneDriveFiles, isUploading: isUploadingFromOneDrive } =
    useOneDriveCompanyDocumentUpload();
  const { pendingPickedItems, clearPendingPickedItems } = useSharePointPicker();

  const [showArchived, setShowArchived] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState<CompanyDocument[]>([]);
  const [documentsToArchive, setDocumentsToArchive] = useState<CompanyDocument[]>([]);
  const [documentsToDelete, setDocumentsToDelete] = useState<CompanyDocument[]>([]);

  const handleFileDrop = async (files: File[]) => {
    for (const file of files) {
      try {
        await createCompanyDocument(file);
      } catch (err) {
        notifyAndLogError(
          'Upload failed',
          `We were not able to upload ${file.name}. Please try again or contact support.`,
          err,
        );
      }
    }
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileDrop(Array.from(files));
    }
    event.target.value = '';
  };

  const handleOneDriveUpload = async (items: PickedItem[]) => {
    try {
      await uploadOneDriveFiles(items);
    } catch (err) {
      notifyAndLogError(
        'Upload failed',
        'We were not able to upload from SharePoint. Please try again or contact support.',
        err,
      );
    }
  };

  useEffect(() => {
    if (pendingPickedItems && pendingPickedItems.length > 0) {
      handleOneDriveUpload(pendingPickedItems);
      clearPendingPickedItems();
    }
  }, [pendingPickedItems, clearPendingPickedItems]);

  const { handleClick: handleSharePointClick } = useSharepoint({
    onPickedItems: handleOneDriveUpload,
    onAccountChange: () => {},
    switchAccountRequested: false,
    onSwitchAccountHandled: () => {},
    fileExtensions: ['.pdf', '.docx'],
  });

  const handleViewDocument = async (doc: CompanyDocument) => {
    try {
      const link = await createLink(doc.file.id);
      window.open(link.url, '_blank');
    } catch (err) {
      notifyAndLogError(
        'Error',
        'We were not able to open the document. Please try again or contact support.',
        err,
      );
    }
  };

  const handleArchiveConfirm = async () => {
    if (documentsToArchive.length === 0) return;
    try {
      await bulkArchiveCompanyDocuments(
        documentsToArchive.map(d => d.id),
        true,
      );
      setDocumentsToArchive([]);
      setSelectedRecords([]);
    } catch (err) {
      notifyAndLogError(
        'Archive failed',
        'We were not able to archive the appendices. Please try again or contact support.',
        err,
      );
    }
  };

  const handleUnarchive = async (docs: CompanyDocument[]) => {
    if (docs.length === 0) return;
    try {
      await bulkArchiveCompanyDocuments(
        docs.map(d => d.id),
        false,
      );
      setSelectedRecords([]);
    } catch (err) {
      notifyAndLogError(
        'Unarchive failed',
        'We were not able to unarchive the appendices. Please try again or contact support.',
        err,
      );
    }
  };

  const handleDeleteConfirm = async () => {
    if (documentsToDelete.length === 0) return;
    try {
      await bulkDeleteCompanyDocuments(documentsToDelete.map(d => d.id));
      setDocumentsToDelete([]);
      setSelectedRecords([]);
    } catch (err) {
      notifyAndLogError(
        'Delete failed',
        'We were not able to delete the appendices. Please try again or contact support.',
        err,
      );
    }
  };

  const filterPredicate = useCallback(
    (item: CompanyDocument, search: string): boolean => {
      const matchesSearch = item.file.fileName.toLowerCase().includes(search.toLowerCase());
      const matchesArchiveFilter = showArchived || !item.isArchived;
      return matchesSearch && matchesArchiveFilter;
    },
    [showArchived],
  );

  const availableDocuments = documents.filter(doc => !doc.isArchived);
  const allDocumentsArchived = documents.length > 0 && availableDocuments.length === 0;

  const columns: DataTableColumn<CompanyDocument>[] = [
    ...(showArchived
      ? [
          {
            accessor: 'isArchived',
            title: 'Status',
            width: 100,
            render: (record: CompanyDocument) =>
              record.isArchived ? (
                <Badge size="xs" color="gray">
                  Archived
                </Badge>
              ) : null,
          },
        ]
      : []),
    {
      accessor: 'file.fileName',
      title: 'Name',
      render: record => (
        <Box>
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
            {record.file.fileName}
          </Text>
          <Text size="xs" c="gray.6">
            {formatFileSize(record.file.fileSize)}
          </Text>
        </Box>
      ),
    },
    {
      accessor: 'uploadedByName',
      title: 'Uploaded by',
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {record.uploadedByName ?? '-'}
        </Text>
      ),
    },
    {
      accessor: 'createdAt',
      title: 'Uploaded',
      render: record => (
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {dayjs(record.createdAt).format('D MMM YYYY')}
        </Text>
      ),
    },
    {
      accessor: 'actions',
      title: '',
      render: record => (
        <AppendixRowActions
          document={record}
          onView={handleViewDocument}
          onArchive={doc => setDocumentsToArchive([doc])}
          onUnarchive={doc => handleUnarchive([doc])}
          onDelete={doc => setDocumentsToDelete([doc])}
          isUpdating={isArchiving}
        />
      ),
    },
  ];

  const isUploadingAny = isUploading || isUploadingFromOneDrive;
  const isBulkPending = isArchiving || isDeleting;
  const allSelectedArchived =
    selectedRecords.length > 0 && selectedRecords.every(d => d.isArchived);
  const allSelectedUnarchived =
    selectedRecords.length > 0 && selectedRecords.every(d => !d.isArchived);

  const SelectionActions = (
    <Group gap="xs">
      <Text size="sm" c="dimmed">
        {selectedRecords.length} selected
      </Text>
      {allSelectedUnarchived && (
        <Button onClick={() => setDocumentsToArchive(selectedRecords)} disabled={isBulkPending}>
          Archive
        </Button>
      )}
      {allSelectedArchived && (
        <Button
          onClick={() => handleUnarchive(selectedRecords)}
          loading={isArchiving}
          disabled={isBulkPending}
        >
          Unarchive
        </Button>
      )}
      {allSelectedArchived && (
        <Button
          color="red"
          onClick={() => setDocumentsToDelete(selectedRecords)}
          disabled={isBulkPending}
        >
          Delete
        </Button>
      )}
      <Button variant="subtle" onClick={() => setSelectedRecords([])} disabled={isBulkPending}>
        Clear
      </Button>
    </Group>
  );

  const UploadMenu = (
    <Menu shadow="md">
      <Menu.Target>
        <Button variant="primary" loading={isUploadingAny}>
          Upload document
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item component="label" closeMenuOnClick={false} style={{ cursor: 'pointer' }}>
          Upload from your computer
          <input
            type="file"
            accept=".pdf,.docx"
            multiple
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
        </Menu.Item>
        <Menu.Item onClick={handleSharePointClick}>Upload from SharePoint</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  const HeaderRow = (
    <>
      <Switch
        label="Show archived"
        checked={showArchived}
        onChange={event => setShowArchived(event.currentTarget.checked)}
        size="sm"
      />
      {selectedRecords.length > 0 ? SelectionActions : UploadMenu}
    </>
  );

  if (!isLoadingDocuments && documents.length === 0) {
    return (
      <AppendicesEmptyState
        variant="no-documents"
        onFileDrop={handleFileDrop}
        onUpload={handleUpload}
        onSharePointClick={handleSharePointClick}
        isUploading={isUploading || isUploadingFromOneDrive}
      />
    );
  }

  if (!isLoadingDocuments && allDocumentsArchived && !showArchived) {
    return (
      <AppendicesEmptyState
        variant="all-archived"
        onFileDrop={handleFileDrop}
        onUpload={handleUpload}
        onSharePointClick={handleSharePointClick}
        onShowArchived={() => setShowArchived(true)}
        isUploading={isUploading || isUploadingFromOneDrive}
      />
    );
  }

  return (
    <>
      <SimpleTable<CompanyDocument>
        resource="company-documents"
        columns={columns}
        title="Appendices"
        HeaderRow={HeaderRow}
        enableSearch
        filterPredicate={filterPredicate}
        paginationDependencies={showArchived}
        pageSize={10}
        mode="client"
        tableProps={{
          selectedRecords,
          onSelectedRecordsChange: setSelectedRecords,
        }}
      />

      <ArchiveAppendixModal
        opened={documentsToArchive.length > 0}
        onClose={() => setDocumentsToArchive([])}
        documents={documentsToArchive}
        onConfirm={handleArchiveConfirm}
        isLoading={isArchiving}
      />

      <ConfirmDeleteModal
        opened={documentsToDelete.length > 0}
        onClose={() => setDocumentsToDelete([])}
        onConfirm={handleDeleteConfirm}
        title={
          documentsToDelete.length === 1
            ? 'Delete appendix'
            : `Delete ${documentsToDelete.length} appendices`
        }
        message={
          documentsToDelete.length === 1
            ? `Are you sure you want to delete ${documentsToDelete[0]?.file.fileName}? This action cannot be undone.`
            : `Are you sure you want to delete these ${documentsToDelete.length} appendices? This action cannot be undone.`
        }
        isLoading={isDeleting}
      />
    </>
  );
}
